<script setup lang="ts">
import {ref, watch} from "vue";
import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import "dayjs/locale/fr";
import {Dialog, DialogHeader, DialogScrollContent, DialogTitle} from "@/core/components/ui/dialog";
import {Label} from "@/core/components/ui/label";
import {Switch} from "@/core/components/ui/switch";
import BaseButton from "@/core/components/base/BaseButton.vue";
import TimePicker from "@/core/components/date-picker/TimePicker.vue";
import {HealthcenterOpening, OpeningDay} from "@/core/interfaces/healthcenter/Healthcenter";
import {OpeningHoursRequest} from "@/core/interfaces/healthcenter/OpeningHoursRequest";
import {healthcenterApi} from "@/container";
import {useToastStore} from "@/stores/toast.store";
import {fetchOpening, refreshHealthcenterOpening} from "@/healthcenter/composables/useHealthcenterOpening";
import {ISO_WEEKDAYS} from "@/agenda/lib/openingHours";

dayjs.extend(localeData);

const {healthcenterId, opening} = defineProps<{
    healthcenterId: number;
    /** The sheet to edit, null for a pharmacy that never filled its hours. */
    opening: HealthcenterOpening | null;
}>();

const emits = defineEmits<{
    /** The sheet as read back after the save, null when that read failed. */
    (event: 'saved', opening: HealthcenterOpening | null): void;
}>();

const open = defineModel<boolean>('open', {required: true});

const {showSuccess, showError} = useToastStore();

const weekdayLabels = dayjs().locale('fr').localeData().weekdays();

const blankDay = (day: number): OpeningDay => ({
    day,
    hours: {morning: {opening: '', closing: ''}, afternoon: {opening: '', closing: ''}}
});

/** A detached copy: the sheet comes from a reactive cache, which structuredClone refuses. */
const copyDay = (day: OpeningDay): OpeningDay => ({
    day: day.day,
    hours: {morning: {...day.hours.morning}, afternoon: {...day.hours.afternoon}}
});

const isOpen = (day: OpeningDay): boolean =>
    !!day.hours.morning.opening || !!day.hours.morning.closing
    || !!day.hours.afternoon.opening || !!day.hours.afternoon.closing;

/**
 * A copy of the sheet, one entry per weekday: the form must not write into
 * the sheet it was given, which a cancelled dialog would leave half-edited.
 */
const draft = ref<OpeningDay[]>([]);
const openStatuses = ref<boolean[]>([]);
const linesEdited = ref<boolean[]>([]);
const saving = ref<boolean>(false);

watch(open, (isOpened) => {
    if (!isOpened) {
        return;
    }

    draft.value = ISO_WEEKDAYS.map((weekday) => {
        const day = opening?.openings.find((entry) => entry.day === weekday);

        return day ? copyDay(day) : blankDay(weekday);
    });
    openStatuses.value = draft.value.map(isOpen);
    linesEdited.value = ISO_WEEKDAYS.map(() => false);
}, {immediate: true});

const copyToAll = (source: OpeningDay) => {
    draft.value = draft.value.map((day, index) => openStatuses.value[index]
        ? {...copyDay(source), day: day.day}
        : day
    );
    linesEdited.value = ISO_WEEKDAYS.map(() => false);
};

/**
 * The request the server expects. A day switched to closed is sent empty
 * whatever its pickers still hold, so that closing it actually closes it.
 */
const toRequest = (): OpeningHoursRequest => draft.value.reduce<OpeningHoursRequest>((request, day, index) => {
    const isDayOpen = openStatuses.value[index];

    return {
        ...request,
        [`d${day.day}_o_m`]: isDayOpen ? day.hours.morning.opening || null : null,
        [`d${day.day}_c_m`]: isDayOpen ? day.hours.morning.closing || null : null,
        [`d${day.day}_o_a`]: isDayOpen ? day.hours.afternoon.opening || null : null,
        [`d${day.day}_c_a`]: isDayOpen ? day.hours.afternoon.closing || null : null
    };
}, {});

const save = async () => {
    if (saving.value) {
        return;
    }

    saving.value = true;

    try {
        // Without a sheet in hand, the server is asked first: a sheet created
        // by a save whose read-back failed is unknown here, and creating
        // another would leave two, of which every reader takes the first.
        const current = opening ?? await fetchOpening(healthcenterId);

        await (current
            ? healthcenterApi().updateOpening(current.id, toRequest())
            : healthcenterApi().createOpening(healthcenterId, toRequest()));
    } catch {
        showError({content: "Les horaires de votre pharmacie n'ont pas pu être mis à jour"});
        saving.value = false;

        return;
    }

    showSuccess({content: "Les horaires de votre pharmacie ont été mis à jour"});

    // Every screen reading the sheet (agenda, planning) follows from the
    // shared cache; a failed read-back only delays that, the save is done.
    const saved = await refreshHealthcenterOpening(healthcenterId).catch(() => null);

    saving.value = false;
    open.value = false;
    emits('saved', saved);
};
</script>

<template>
  <Dialog v-model:open="open">
    <DialogScrollContent class="max-w-fit">
      <DialogHeader>
        <DialogTitle>
          Mettre à jour vos horaires
        </DialogTitle>
      </DialogHeader>
      <div>
        <div v-for="(day, index) in draft" :key="day.day">
          <div class="relative flex flex-row items-end gap-10 py-4">
            <div class="flex flex-1 flex-row gap-10">
              <div class="flex flex-2 capitalize">
                <Label class="mb-1">{{ weekdayLabels[day.day % 7] }}</Label>
              </div>
              <div class="flex flex-1 justify-center gap-1">
                <Switch v-model="openStatuses[index]" class="mb-1.5" />
                <Label>{{ openStatuses[index] ? "Ouvert" : "Fermé" }}</Label>
              </div>
            </div>
            <div class="flex flex-col items-center gap-1">
              <Label v-if="index === 0">Matin</Label>
              <div class="flex flex-row gap-5">
                <TimePicker
                  v-model="day.hours.morning.opening"
                  :disabled="!openStatuses[index]"
                  @on-update="linesEdited[index] = true"
                />
                <TimePicker
                  v-model="day.hours.morning.closing"
                  :disabled="!openStatuses[index]"
                  @on-update="linesEdited[index] = true"
                />
              </div>
            </div>
            <div class="flex flex-col items-center gap-1">
              <Label v-if="index === 0">Après-midi</Label>
              <div class="flex flex-row gap-5">
                <TimePicker
                  v-model="day.hours.afternoon.opening"
                  :disabled="!openStatuses[index]"
                  @on-update="linesEdited[index] = true"
                />
                <TimePicker
                  v-model="day.hours.afternoon.closing"
                  :disabled="!openStatuses[index]"
                  @on-update="linesEdited[index] = true"
                />
              </div>
            </div>
            <div class="absolute -bottom-3 right-0">
              <div v-if="linesEdited[index] && isOpen(day)" @click="() => copyToAll(day)">
                <p class="cursor-pointer font-semibold text-primary hover:opacity-75">
                  Cliquez ici pour appliquer ces horaires chaque jour
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="flex justify-end pt-2">
          <BaseButton primary :disabled="saving" @click="save">
            Enregistrer
          </BaseButton>
        </div>
      </div>
    </DialogScrollContent>
  </Dialog>
</template>
